import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Reveal, Overline } from "../components/site/Reveal";
import FocalImage from "../components/FocalImage";

const focal = (x) => ({ url: x.image, ...(x.image_focal || {}) });

const CATS = ["All", "Career", "Leadership", "Community"];

export default function Media() {
  const { content, lang, t } = useLang();
  const m = content.media || {};
  const press = content.press_logos || [];
  const [blogs, setBlogs] = useState([]);
  const [pods, setPods] = useState([]);
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get(`/media?kind=blog&category=${cat}`).then((r) => setBlogs(r.data)).catch(() => {});
    api.get("/media?kind=podcast").then((r) => setPods(r.data)).catch(() => {});
  }, [cat]);

  const featured = blogs.find((b) => b.featured) || blogs[0];
  const rest = blogs.filter((b) => b.id !== featured?.id);

  return (
    <div data-testid="media-page">
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-12">
        <Reveal><Overline>{m.hero_overline}</Overline></Reveal>
        <Reveal delay={0.1}><h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.05] mt-5 max-w-4xl text-primary" data-testid="media-title">{m.hero_title}</h1></Reveal>
        <Reveal delay={0.2}><p className="text-lg text-foreground/70 mt-6 max-w-2xl">{m.hero_body}</p></Reveal>
      </section>

      <section className="border-y border-primary/10 py-8">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {press.map((p, i) => <span key={i} className="font-display text-xl md:text-2xl text-primary/40 font-medium">{p}</span>)}
        </div>
      </section>

      {featured && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-14">
          <Reveal>
            <Link to={`/media/${featured.id}`} data-testid="featured-blog" className="grid lg:grid-cols-2 gap-8 items-center bg-card border border-primary/10 rounded-[2rem] overflow-hidden group">
              <div className="aspect-[4/3] lg:h-full overflow-hidden"><div className="w-full h-full group-hover:scale-105 transition-transform duration-500"><FocalImage img={focal(featured)} alt="" /></div></div>
              <div className="p-8 md:p-12">
                <span className="text-xs uppercase tracking-widest text-secondary font-semibold">{t("From the blog", "Du blogue")} · {featured.read_time}</span>
                <h2 className="font-display text-3xl md:text-4xl font-medium text-primary mt-3">{lang === "fr" ? featured.title_fr : featured.title_en}</h2>
                <p className="text-foreground/70 mt-3">{lang === "fr" ? featured.excerpt_fr : featured.excerpt_en}</p>
                <span className="inline-flex items-center gap-2 mt-6 font-semibold text-primary group-hover:text-secondary transition-colors">{t("Read the full story", "Lire l'article")} <ArrowRight size={16} /></span>
              </div>
            </Link>
          </Reveal>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-14">
        <div className="flex flex-wrap gap-3 mb-8">
          {CATS.map((cc) => (
            <button key={cc} onClick={() => setCat(cc)} data-testid={`media-cat-${cc}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${cat === cc ? "bg-primary text-primary-foreground" : "border border-primary/20 text-primary hover:bg-primary/5"}`}>{cc}</button>
          ))}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((b, i) => (
            <Reveal key={b.id} delay={i * 0.06}>
              <Link to={`/media/${b.id}`} data-testid={`media-card-${b.id}`} className="block bg-card border border-primary/10 rounded-3xl overflow-hidden group h-full hover:-translate-y-1 transition-transform duration-300">
                <div className="aspect-[16/10] overflow-hidden"><div className="w-full h-full group-hover:scale-105 transition-transform duration-500"><FocalImage img={focal(b)} alt="" /></div></div>
                <div className="p-6">
                  <span className="text-xs uppercase tracking-widest text-secondary font-semibold">{b.category} · {b.read_time}</span>
                  <h3 className="font-display text-xl font-semibold text-primary mt-2">{lang === "fr" ? b.title_fr : b.title_en}</h3>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-primary text-primary-foreground py-20 grain">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Overline>{t("The GYBC Podcast", "Le balado GYBC")}</Overline>
          <h2 className="font-display text-4xl md:text-5xl font-medium mt-4 mb-10">{t("Conversations worth hearing.", "Des conversations à écouter.")}</h2>
          <div className="space-y-4">
            {pods.map((p) => (
              <div key={p.id} className="flex items-center gap-5 bg-primary-foreground/5 border border-primary-foreground/10 rounded-2xl p-5 hover:bg-primary-foreground/10 transition-colors">
                <button className="w-12 h-12 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center shrink-0"><Play size={20} fill="currentColor" /></button>
                <div className="flex-1">
                  <p className="font-semibold">{lang === "fr" ? p.title_fr : p.title_en}</p>
                  <p className="text-sm text-primary-foreground/60">{lang === "fr" ? p.excerpt_fr : p.excerpt_en}</p>
                </div>
                <span className="text-sm text-primary-foreground/60 hidden sm:block">{p.read_time}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
